import React, { useState } from "react";
import { Button, Input, Label, Dropdown, Option } from "@fluentui/react-components";
import { Add20Regular, Delete20Regular, Edit20Regular } from "@fluentui/react-icons";
import { useTranslation } from "react-i18next";
import QueueSelectorComponent from "../../GenericComponent/QueueSelectorComponent";
import "../../CSS/RoutingChatComponents.css";

export default function CustomerChoiceChatComponent({ routing, updateRouting, chatFlowData }) {
  const { t } = useTranslation();
  const [editingAction, setEditingAction] = useState(null);

  // Exclure routingName de l'objet routing
  const { routingName, ...routingData } = routing;
  
  
  const updateField = (field, value) => {
    updateRouting({ ...routingData, [field]: value });
  };

  const addAction = () => {
    const newId = Date.now().toString();
    const newActions = {
      ...(routingData.customerChoiceActions || {}),
      [newId]: {
        text: "",
        displayText: "",
        title: "",
        value: newId,
        type: "postBack"
      }
    };
    updateField("customerChoiceActions", newActions);
    setEditingAction(newId);
  };

  const updateAction = (actionId, field, value) => {
    const currentAction = (routingData.customerChoiceActions || {})[actionId];
    
    const updatedActions = {
      ...(routingData.customerChoiceActions || {}),
      [actionId]: {
        ...(routingData.customerChoiceActions || {})[actionId],
        [field]: value,
        // Si on change title, mettre à jour text et displayText aussi
        ...(field === 'title' ? { text: value, displayText: value } : {})
      }
    };
    
    // Mettre à jour immédiatement le routing
    const updatedRouting = { ...routingData, customerChoiceActions: updatedActions };
    updateRouting(updatedRouting);
  };

  const updateActionNext = (actionId, nextType, nextValue) => {
    const currentAction = (routingData.customerChoiceActions || {})[actionId];
    let nextObject = undefined;
    
    if (nextType) {
      nextObject = {
        type: nextType,
      };
      
      if (nextType === "queue") {
        // nextValue est un objet {queueId, queueDisplayName} ou les valeurs existantes
        if (typeof nextValue === "object" && nextValue !== null) {
          nextObject.queueId = nextValue.queueId || "";
          nextObject.queueDisplayName = nextValue.queueDisplayName || "";
        } else {
          nextObject.queueId = currentAction.next?.queueId || "";
          nextObject.queueDisplayName = currentAction.next?.queueDisplayName || "";
        }
      } else if (nextType === "routing") {
        nextObject.routingName = nextValue || "";
      }
    }
    
    const updatedActions = {
      ...(routingData.customerChoiceActions || {}),
      [actionId]: {
        ...currentAction,
        next: nextObject
      }
    };
    updateField("customerChoiceActions", updatedActions);
  };

  const deleteAction = (actionId) => {
    const updatedActions = { ...(routingData.customerChoiceActions || {}) };
    delete updatedActions[actionId];
    updateField("customerChoiceActions", updatedActions);
    if (editingAction === actionId) setEditingAction(null);
  };

  const getAvailableRoutings = () => {
    return Object.keys(chatFlowData.routing || {}).filter(key => key !== routingName);
  };

  const getRoutingDisplayName = (routingKey) => {
    if (routingKey === "0") return "START";
    const routingObj = chatFlowData.routing?.[routingKey];
    if (!routingObj) return routingKey;
    
    // Retourner displayName s'il existe, sinon le routingKey
    return routingObj.displayName || routingKey;
  };

  return (
    <div className="routing-chat-container">
      <div className="routing-stacked-fields">
        <div className="routing-field-stacked">
          <Label>{t("NAME")}</Label>
          <Input
            value={routingData.customerChoiceName || ""}
            onChange={(e) => updateField("customerChoiceName", e.target.value)}
            placeholder={t("ENTER_NAME")}
          />
        </div>

        <div className="routing-field-stacked">
          <Label>{t("TEXT")}</Label>
          <Input
            value={routingData.customerChoiceText || ""}
            onChange={(e) => updateField("customerChoiceText", e.target.value)}
            placeholder={t("ENTER_QUESTION_TEXT")}
          />
        </div>
      </div>

      <div className="routing-actions-header">
        <h4>{t("RETURN_MESSAGES")}</h4>
        <Button   
         appearance="primary" icon={<Add20Regular />} onClick={addAction}>
        </Button>
      </div>

      <div className="routing-grid-container">
        {/* Header */}
        <div className="routing-grid-header customer-choice-grid-header">
          <span>{t("CHOOSE_NAME")}</span>
          <span>{t("NEXT")}</span>
          <span>{t("REDIRECT_TO")}</span>
          <span>{t("ACTIONS")}</span>
        </div>

        {/* Rows */}
        {Object.entries(routingData.customerChoiceActions || {}).map(([actionId, action]) => {
          return (
          <div key={actionId} className="routing-grid-row customer-choice-grid-row">
            <div className="routing-grid-cell">
              <Input
                value={action?.title || ''}
                onChange={(e, data) => {
                  // Fluent UI Input passe parfois la valeur dans data.value
                  const newValue = data?.value ?? e?.target?.value ?? '';
                  // Mise à jour immédiate du state local pour l'affichage
                  const updatedActions = {
                    ...routingData.customerChoiceActions,
                    [actionId]: {
                      ...routingData.customerChoiceActions[actionId],
                      title: newValue,
                      text: newValue,
                      displayText: newValue
                    }
                  };
                  updateField("customerChoiceActions", updatedActions);
                }}
                onBlur={(e) => {
                  const finalValue = (e.target.value || '').trim();
                  if (finalValue) {
                    updateAction(actionId, 'title', finalValue);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    const finalValue = (e.target.value || '').trim();
                    if (finalValue) {
                      updateAction(actionId, 'title', finalValue);
                    }
                    e.target.blur(); // Retirer le focus
                  }
                }}
                placeholder={t("ENTER_CHOICE_NAME")}
              />
            </div>
            
            <div className="routing-grid-cell">
              <Dropdown
                value={action.next?.type || ""}
                selectedOptions={action.next?.type ? [action.next.type] : []}
                onOptionSelect={(_, data) => {
                  const selectedType = data.optionValue;
                  if (!selectedType || selectedType === "") {
                    updateActionNext(actionId, null, null);
                  } else if (selectedType === "routing") {
                    updateActionNext(actionId, selectedType, action.next?.routingName || "");
                  } else if (selectedType === "queue") {
                    updateActionNext(actionId, selectedType, {
                      queueId: action.next?.queueId || "",
                      queueDisplayName: action.next?.queueDisplayName || ""
                    });
                  }
                }}
                placeholder={t("SELECT_NEXT_TYPE")}
              >
                <Option value="">{t("NONE")}</Option>
                <Option value="routing">{t("ROUTING")}</Option>
                <Option value="queue">{t("QUEUE")}</Option>
              </Dropdown>
            </div>
            
            <div className="routing-grid-cell">
              {action.next?.type === "routing" && (
                <Dropdown
                  value={getRoutingDisplayName(action.next.routingName) || ""}
                  selectedOptions={action.next.routingName ? [action.next.routingName] : []}
                  onOptionSelect={(_, data) => updateActionNext(actionId, "routing", data.optionValue)}
                  placeholder={t("SELECT_ROUTING")}
                >
                  {getAvailableRoutings().map(routingKey => (
                    <Option key={routingKey} value={routingKey} text={getRoutingDisplayName(routingKey)}>
                      {getRoutingDisplayName(routingKey)}
                    </Option>
                  ))}
                </Dropdown>
              )}
              {action.next?.type === "queue" && (
                <QueueSelectorComponent
                  displayName={action.next.queueDisplayName || ""}
                  getData={(queueData) => {
                    updateActionNext(actionId, "queue", {
                      queueId: queueData.id,
                      queueDisplayName: queueData.displayName
                    });
                  }}
                />
              )}
            </div>
            
            <Button
              icon={<Delete20Regular />}
              appearance="subtle"
              onClick={() => deleteAction(actionId)}
            />
          </div>
          );
        })}
      </div>
    </div>
  );
}
